import React, { useContext, useState } from 'react';
import { TextField, Stack } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import { isEmpty } from 'lodash';
import Dropdown from 'Components/Dropdown/index';
import { useAppSelector } from 'hooks/index';
import { selectUserData } from 'store/user/userSelector';
import validateEmailAddress from 'Utils/validateEmailAddress';
import { useNotificationContext } from 'app/contexts/NotificationContext/index';
import { DropdownOption } from 'Interfaces/Dropdown/SelectDropdownInterfaces';
import UserContext from '../../context/UsersContext';
import {
    FirestoreUser,
    FirestoreUserRole,
    InvitedUser,
    useCreateBotUserMutation,
    useSendEmailInviteMutation,
} from 'generated/graphql';
import {
    selectPartnerData,
    selectTeamsForTeamSelector,
} from 'store/partner/partnerSelector';
import ConfirmationModal from "Components/Common/ConfirmationModal";

const userRolesOptions: DropdownOption[] = [
    {
        label: 'User',
        value: FirestoreUserRole.User,
    },
    {
        label: 'Organization Admin',
        value: FirestoreUserRole.OrganizationAdmin,
    },
];

interface SendEmailInvitationProps {
    userType?: 'bot' | 'agent'
}

const SendEmailInvitation: React.FC<SendEmailInvitationProps> = ({ userType = 'agent' }) => {

    const { showNotification } = useNotificationContext();
    const { selectedInvitationType, setSelectedInvitationType, addInvitation, addUser } = useContext(UserContext);

    const { uid: userId } = useAppSelector(selectUserData);
    const { instanceId: organizationId } = useAppSelector(selectPartnerData);
    const teams = useAppSelector(selectTeamsForTeamSelector);
    const teamOptions: DropdownOption[] = teams.map(({ name, key }) => ({
        label: name,
        value: key,
    }));

    const [showModal, setShowModal] = useState(true);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [teamId, setTeamId] = useState(teamOptions[0]?.value || '');
    const [role, setRole] = useState(userRolesOptions[0].value);

    const [sendUserInvitations, { loading }] = useSendEmailInviteMutation({
        onCompleted: () => {
            showNotification({
                message: `We've sent invitation to ${email}`,
                severity: 'success',
            });
        },
        onError: () => {
            showNotification({
                message: 'Unable to send email invitation',
                severity: 'error',
            });
        },
    });

    const [creteBotUser, { loading: isCreatingBotUser }] = useCreateBotUserMutation({
        onCompleted: () => {
            showNotification({
                message: `We've added virtual agent ${email}`,
                severity: 'success',
            });
        },
        onError: () => {
            showNotification({
                message: 'Unable to create virtual agent',
                severity: 'error',
            });
        },
    });

    const validateEmail = (email: string) => {
        const isValidEmail = validateEmailAddress(email);
        setEmailError(!isValidEmail || isEmpty(email));
    };

    const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const emailValue = event.target.value.trim().toLowerCase();
        setEmail(emailValue);
        if (emailError) {
            validateEmail(emailValue);
        }
    };

    const onEmailBlur = () => {
        validateEmail(email);
    };

    const handleTeamChange = (event: SelectChangeEvent) => {
        const teamId = event.target.value;
        setTeamId(teamId);
    };

    const handleRoleChange = (event: SelectChangeEvent): void => {
        const selectedUserRole = event.target.value;
        setRole(selectedUserRole);
    };

    const handleSend = async () => {
        try {
            if (!email) {
                setEmailError(true);
                return;
            }

            const invitation: InvitedUser = {
                email,
                role: role as FirestoreUserRole,
            };

            if (userType === 'bot') {
                const { data: { createBotUser } } = await creteBotUser({
                    variables: {
                        createUserInput: {
                            email,
                            organizationId,
                            teamId,
                            roles: [invitation.role],
                            createdBy: userId,
                        }
                    },
                });
                addUser(createBotUser as FirestoreUser);
            } else {
                const {
                    data: { sendEmailInvite: invitesSent },
                } = await sendUserInvitations({
                    variables: {
                        input: {
                            instanceId: organizationId,
                            teamId,
                            inviterId: userId,
                            invitations: [invitation],
                        },
                    },
                });

                addInvitation(invitesSent);
            }
            handleOnCancel();
        } catch (error) {
            console.error(
                'something went wrong while sending an email invitation',
                error,
                error,
            );
        }
    };

    const handleOnCancel = () => {
        setShowModal(false);
        setSelectedInvitationType(null);
    }

    return (
        <ConfirmationModal
            open={showModal}
            onClose={handleOnCancel}
            title={selectedInvitationType}
            content={
                <Stack spacing={2}>
                    <TextField
                        onBlur={onEmailBlur}
                        error={emailError}
                        label="Email Address"
                        helperText={
                            emailError
                                ? 'Please enter a valid email address.'
                                : 'We’ll wait while you head off to copy and paste'
                        }
                        required
                        type="email"
                        value={email}
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => onEmailChange(event)}
                    />
                    <Dropdown
                        value={teamId}
                        options={teamOptions}
                        helperText="Which team will they be invited to?"
                        onChange={handleTeamChange}
                    />
                    <Dropdown
                        value={role}
                        options={userRolesOptions}
                        helperText="What level of access will they have?"
                        onChange={handleRoleChange}
                    />
                </Stack>
            }
            footer={{
                primaryButton: {
                    label: userType === 'bot' ? 'ADD' : 'SEND INVITE',
                    onClick: handleSend,
                    disabled: emailError || loading || isCreatingBotUser,
                    loading: loading || isCreatingBotUser
                },
                secondaryButton: {
                    label: 'Cancel',
                    onClick: handleOnCancel,
                }
            }}
        >
        </ConfirmationModal>
    );
};

export default SendEmailInvitation;
